import React from 'react';
import { connect } from 'react-redux';
import { withCookies } from 'react-cookie';
import { setCampaignCanvassersLoading } from '../../store/actions';
import {
  pullCampaignCanvassers,
  pullCampaignOffices,
  updateOrCreateCanvasser,
} from '../../store/api/calls';
import { canvasserEditCreateForm } from '../../utils/forms/formFields';

import NavBaseTemplate from '../templates/NavBaseTemplate';
import BaseForm from '../theme/forms/BaseForm';
import DataTable from '../theme/tables/DataTable';
import BaseModal from '../theme/modals/BaseModal';
import FeedbackTile from '../theme/modals/FeedbackTile';
import Moment from 'moment';

class CanvasserScreen extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      editModalOpen: false,
      deleteModalOpen: false,
      editingInstance: {},
      error: false,
      errMsg: '',
    };

    this.formInputs = canvasserEditCreateForm;
  }

  mountOrUpdatePull = () => {
    Promise.all([pullCampaignCanvassers(), pullCampaignOffices()]).then(() =>
      this.populateCreateEditFormWithOffices()
    );
  };

  componentDidMount() {
    this.mountOrUpdatePull();
  }

  componentDidUpdate(prevProps) {
    if (this.props.focusedCampaign !== prevProps.focusedCampaign) {
      this.mountOrUpdatePull();
    }
  }

  populateCreateEditFormWithOffices() {
    const offices = [{ value: null, display: '' }];
    this.props.campaignOffices.forEach((d) => {
      offices.push({ value: d.id, display: d.name });
    });

    this.formInputs = [...canvasserEditCreateForm];
    this.formInputs.push({
      type: 'select',
      title: 'Office',
      dataKey: 'office',
      options: offices,
    });
  }

  _convertOfficeIDtoOfficeObj = (id) => {
    return this.props.campaignOffices.find((d) => d.id === id) || id;
  };

  /* -----------------------------
   * Table Data
   * ----------------------------- */

  _filterKeysForDataTable() {
    return this.props.canvassers.map((d) => ({
      office:
        typeof this._convertOfficeIDtoOfficeObj(d.office) === 'object'
          ? this._convertOfficeIDtoOfficeObj(d.office).name
          : d.office,

      first_name: d.firstname,
      last_name: d.lastname,
      username: d.username,

      is_training: d.is_training ? <span className="danger-text">Yes</span> : '',
      _is_training: d.is_training,

      created_on: new Moment(d.created_at).format('MM/DD/YY'),
      _created_on: new Moment(d.created_at),

      // RIGHT-MOST ACTION COLUMN — NO HEADER
      '\u200B': (
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
          <button
            className="et-datatable-button button button-outline"
            onClick={() => this.onOpenEditModal(d)}
          >
            Edit
          </button>
          <button
            className="et-datatable-button et-datatable-button-red button button-outline"
            onClick={() => this.onOpenDeleteModal(d)}
          >
            Remove
          </button>
        </div>
      ),

      _data: d,
    }));
  }

  /* -----------------------------
   * Edit / Delete
   * ----------------------------- */

  onOpenEditModal = (modelInstance = {}) => {
    if (modelInstance.office && typeof modelInstance.office === 'object') {
      modelInstance = { ...modelInstance, office: modelInstance.office.id };
    }

    this.buildInputsWithPrepopulatedFields(modelInstance);
    this.setState({ editModalOpen: true, editingInstance: modelInstance });
  };

  onCloseEditModal = () => {
    this.setState({ editModalOpen: false, editingInstance: {} });
  };

  onOpenDeleteModal = (modelInstance = {}) => {
    this.setState({ deleteModalOpen: true, editingInstance: modelInstance });
  };

  onCloseDeleteModal = () => {
    this.setState({ deleteModalOpen: false, editingInstance: {} });
  };

  buildInputsWithPrepopulatedFields = (modelInstance) => {
    const isCreate = !modelInstance.id;

    this.formInputs.forEach((d) => {
      d.value = modelInstance[d.dataKey];
      if (d.dataKey === 'password') {
        d.placeholder = isCreate
          ? 'Required for new canvassers'
          : 'Leave blank if you do not want to change this.';
      }
    });
  };

  formatApiErrorMessage = (data) => {
    if (!data) return 'Server Error';
    if (typeof data === 'string') return data;

    if (typeof data === 'object') {
      return Object.entries(data)
        .map(([field, errors]) => {
          const label = field.replace(/_/g, ' ');
          const message = Array.isArray(errors) ? errors.join(', ') : String(errors);
          return `${label}: ${message}`;
        })
        .join('; ');
    }

    return String(data);
  };

  handleUpdateCreateSubmission = (data) => {
    const id = this.state.editingInstance.id || null;
    const submissionData = id ? { ...data, id } : { ...data };

    if (!submissionData.password) delete submissionData.password;

    this.setState({ error: false });

    return updateOrCreateCanvasser(submissionData)
      .then(() => {
        this.onCloseEditModal();
      })
      .catch((err) => {
        this.setErrorMessage(this.formatApiErrorMessage(err.response?.data));
        this.buildInputsWithPrepopulatedFields(submissionData);
        this.setState({
          editModalOpen: true,
          editingInstance: id ? { ...submissionData, id } : submissionData,
        });
      })
      .then(() => {
        this.props.setCampaignCanvassersLoading(false);
      });
  };

  handleRemoveSubmission = () => {
    const id = this.state.editingInstance.id;
    updateOrCreateCanvasser({ id, is_active: 0 }).then(this.onCloseDeleteModal);
  };

  /* -----------------------------
   * Errors
   * ----------------------------- */

  _isError = () => this.props.error || this.state.error;

  setErrorMessage = (msg) => {
    this.setState({ error: true, errMsg: msg });

    setTimeout(() => {
      this.setState({ error: false, errMsg: '' });
    }, 3500);
  };

  /* -----------------------------
   * Render
   * ----------------------------- */

  render() {
    return (
      <NavBaseTemplate>
        <div className="button-table-top-wrapper">
          <a className="button" onClick={() => this.onOpenEditModal({})}>
            + Create Canvasser
          </a>
        </div>

        <DataTable data={this._filterKeysForDataTable() || []} title="Canvassers" />

        <FeedbackTile
          text={
            <div>
              <i className="fas fa-asterisk fa-spin" style={{ marginRight: '15px' }} />
              Loading...
            </div>
          }
          visible={this.props.loading}
        />

        <FeedbackTile
          text={
            <div>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '15px' }} />
              {this.state.errMsg || 'There was an error.'}
            </div>
          }
          visible={this._isError()}
          classNames="danger"
        />

        {/* EDIT MODAL */}
        <BaseModal open={this.state.editModalOpen} onClose={this.onCloseEditModal} center>
          <BaseForm
            inputs={this.formInputs}
            title={
              this.state.editingInstance.firstname
                ? `Edit Canvasser: ${this.state.editingInstance.firstname} ${this.state.editingInstance.lastname}`
                : 'Create Canvasser'
            }
            onCancel={this.onCloseEditModal}
            onSubmit={this.handleUpdateCreateSubmission}
          />
        </BaseModal>

        {/* DELETE MODAL */}
        <BaseModal open={this.state.deleteModalOpen} onClose={this.onCloseDeleteModal} center>
          <div className="modal-internal-wrapper">
            <p>
              Are you sure you want to remove the canvasser{' '}
              <b>
                {this.state.editingInstance.firstname} {this.state.editingInstance.lastname}
              </b>
              ?
            </p>
            <div className="button-wrapper">
              <div className="button" onClick={this.handleRemoveSubmission}>
                Remove
              </div>
              <div className="button button-outline" onClick={this.onCloseDeleteModal}>
                Cancel
              </div>
            </div>
          </div>
        </BaseModal>
      </NavBaseTemplate>
    );
  }
}

/* -----------------------------
 * Redux
 * ----------------------------- */

const mapStateToProps = (state) => ({
  focusedCampaign: state.session.focusedCampaign,
  canvassers: state.canvassers.data,
  loading: state.canvassers.loading,
  error: state.canvassers.error,
  campaignOffices: state.offices.data,
  is_admin: state.permissions.is_admin,
});

const mapDispatchToProps = (dispatch) => ({
  setCampaignCanvassersLoading: (bool) => dispatch(setCampaignCanvassersLoading(bool)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withCookies(CanvasserScreen));
